import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

interface LaTeXRendererProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * LaTeX Renderer Component
 * Automatically detects and renders LaTeX expressions in text
 * - Inline math: $expression$ or \(expression\)
 * - Display math: $$expression$$ or \[expression\]
 */
const LaTeXRenderer: React.FC<LaTeXRendererProps> = ({ children, className }) => {
  
  /**
   * Auto-wrap common LaTeX expressions that aren't already wrapped
   */
  const autoWrapLatexExpressions = (text: string): string => {
    // Don't process text that already has LaTeX delimiters
    if (text.includes('$') || text.includes('\\(') || text.includes('\\[')) {
      return text;
    }

    // First, normalize double backslashes to single backslashes for LaTeX commands
    let processedText = text.replace(/\\\\([a-zA-Z]+)/g, '\\$1');

    // Patterns for common LaTeX expressions
    const patterns = [
      // Powers: x^2, x^{-1}, etc.
      { regex: /([a-zA-Z0-9])\^(\{[^}]+\}|\d+|[a-zA-Z])/g, replacement: '$1^{$2}' },
      
      // Subscripts: H_2O, x_1, etc.
      { regex: /([a-zA-Z0-9])_(\{[^}]+\}|\d+|[a-zA-Z])/g, replacement: '$1_{$2}' },
      
      // Fractions: \frac{a}{b}
      { regex: /\\frac\{([^}]+)\}\{([^}]+)\}/g, replacement: '\\frac{$1}{$2}' },
      
      // Greek letters
      { regex: /\\(alpha|beta|gamma|delta|epsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|omicron|pi|rho|sigma|tau|upsilon|phi|chi|psi|omega)/g, replacement: '\\$1' },
      
      // Mathematical operators
      { regex: /\\times/g, replacement: '\\times' },
      { regex: /\\div/g, replacement: '\\div' },
      { regex: /\\pm/g, replacement: '\\pm' },
      
      // Arrows
      { regex: /\\(rightarrow|leftarrow|leftrightarrow|Rightarrow|Leftarrow|Leftrightarrow)/g, replacement: '\\$1' },
      
      // Square roots
      { regex: /\\sqrt\{([^}]+)\}/g, replacement: '\\sqrt{$1}' },
      
      // Absolute values
      { regex: /\|([^|]+)\|/g, replacement: '|$1|' }
    ];

    // Apply auto-wrapping for mathematical expressions
    patterns.forEach(pattern => {
      processedText = processedText.replace(pattern.regex, `$${pattern.replacement}$`);
    });

    return processedText;
  };

  /**
   * Main LaTeX rendering function
   */
  const renderLatex = (text: string): React.ReactNode[] => {
    // Auto-wrap expressions if needed
    const processedText = autoWrapLatexExpressions(text);
    
    // Handle display math first ($$...$$)
    const displayMathRegex = /\$\$(.*?)\$\$/g;
    let parts = processedText.split(displayMathRegex);
    let result: React.ReactNode[] = [];
    
    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 0) {
        // Regular text or inline math
        const textPart = parts[i];
        if (textPart) {
          result.push(...renderInlineMath(textPart, i));
        }
      } else {
        // Display math
        const mathExpression = parts[i];
        if (mathExpression) {
          try {
            result.push(
              <div key={`display-${i}`} style={{ margin: '16px 0', textAlign: 'center' }}>
                <BlockMath>{mathExpression}</BlockMath>
              </div>
            );
          } catch (error) {
            console.warn('LaTeX display math rendering error:', error);
            result.push(
              <div key={`display-error-${i}`} style={{ 
                color: 'red', 
                fontStyle: 'italic',
                margin: '16px 0',
                textAlign: 'center'
              }}>
                [LaTeX Error: {mathExpression}]
              </div>
            );
          }
        }
      }
    }
    
    return result;
  };

  const renderInlineMath = (text: string, baseKey: number = 0) => {
    // Ensure text is a string
    if (!text || typeof text !== 'string') {
      return [<span key={`text-${baseKey}-fallback`}>{text}</span>];
    }

    // Handle inline math with $ or \( \)
    const inlineMathRegex = /\$([^$]+)\$|\\\((.*?)\\\)/g;
    const parts = text.split(inlineMathRegex);
    const result: React.ReactNode[] = [];
    
    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === undefined) continue;
      
      if (i % 3 === 0) {
        // Regular text
        if (parts[i]) {
          result.push(<span key={`text-${baseKey}-${i}`}>{parts[i]}</span>);
        }
      } else if (i % 3 === 1 || i % 3 === 2) {
        // Math expression (either $...$ or \(...\))
        const mathExpression = parts[i];
        if (mathExpression) {
          try {
            result.push(
              <InlineMath key={`inline-${baseKey}-${i}`}>
                {mathExpression}
              </InlineMath>
            );
          } catch (error) {
            console.warn('LaTeX inline math rendering error:', error);
            result.push(
              <span key={`inline-error-${baseKey}-${i}`} style={{ 
                color: 'red', 
                fontStyle: 'italic' 
              }}>
                [LaTeX Error: {mathExpression}]
              </span>
            );
          }
        }
      }
    }
    
    return result;
  };

  return (
    <span className={className}>
      {typeof children === 'string' ? renderLatex(children) : children}
    </span>
  );
};

export default LaTeXRenderer;